import React, { useMemo, useState } from 'react';
import { Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import { MessageCircle, X } from 'lucide-react-native';
import {
  Box,
  Button,
  ButtonText,
  HStack,
  Icon,
  Input,
  InputField,
  Pressable,
  ScrollView,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import MarkdownText from '@/common/components/MarkdownText';
import ConfirmCard from './ConfirmCard';
import ToolGroupCard from './ToolGroupCard';
import { useAssistantStream, type AssistantStreamItem } from './useAssistantStream';

type ActivityItem = Extract<AssistantStreamItem, { kind: 'activity' }>;

type RenderSegment =
  | { type: 'single'; item: Exclude<AssistantStreamItem, { kind: 'activity' }> }
  | {
    type: 'activityGroup';
    key: string;
    activities: Array<{
      activityType: 'tool_call' | 'tool_result';
      operation: string;
      summary?: string;
      error?: string;
    }>;
  };

const groupStreamItems = (items: AssistantStreamItem[]): RenderSegment[] => {
  const segments: RenderSegment[] = [];
  let activityBuffer: ActivityItem[] = [];

  const flushActivities = () => {
    if (activityBuffer.length === 0) return;
    segments.push({
      type: 'activityGroup',
      key: `activity-group-${activityBuffer[0].id}`,
      activities: activityBuffer.map(({
        activityType,
        operation,
        summary,
        error,
      }) => ({
        activityType,
        operation,
        summary,
        error,
      })),
    });
    activityBuffer = [];
  };

  items.forEach((item) => {
    if (item.kind === 'activity') {
      if (
        activityBuffer.length > 0
        && activityBuffer[0].turnId !== item.turnId
      ) {
        flushActivities();
      }
      activityBuffer.push(item);
      return;
    }
    flushActivities();
    segments.push({ type: 'single', item });
  });

  flushActivities();
  return segments;
};

/**
 * AdminAssistant
 * ----
 * Collapsible bottom-right chat popover for admin/lecturer operations assistant.
 */
const AdminAssistant: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const {
    items,
    pendingConfirmation,
    isStreaming,
    sendUserMessage,
    confirmPending,
  } = useAssistantStream();

  const segments = useMemo(() => groupStreamItems(items), [items]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || isStreaming) return;
    setDraft('');
    await sendUserMessage(text);
  };

  const handleKeyPress = (
    event: {
      nativeEvent?: { key?: string; shiftKey?: boolean };
      key?: string;
      shiftKey?: boolean;
      preventDefault?: () => void;
    },
  ) => {
    if (Platform.OS !== 'web') return;
    const key = event?.nativeEvent?.key || event?.key;
    const shift = event?.nativeEvent?.shiftKey || event?.shiftKey;
    if (key === 'Enter' && !shift) {
      event.preventDefault?.();
      handleSend();
    }
  };

  return (
    <Box position="absolute" bottom="$4" right="$4" zIndex={50}>
      {!!isOpen && (
        <Box
          w={420}
          h={480}
          bg="$bgCard"
          borderWidth={1}
          borderColor="$borderLight"
          borderRadius="$md"
          softShadow="3"
          mb="$2"
          testID="admin-assistant-panel"
        >
          <HStack
            px="$3"
            py="$2"
            borderBottomWidth={1}
            borderColor="$borderLight"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text size="sm" fontWeight="semibold">{t('admin.assistant.title')}</Text>
            <Pressable onPress={() => setIsOpen(false)} testID="admin-assistant-close">
              <Icon as={X} size="sm" color="$textSubtle" />
            </Pressable>
          </HStack>

          <ScrollView flex={1} px="$3" py="$2">
            <VStack space="sm">
              {segments.map((segment) => {
                if (segment.type === 'activityGroup') {
                  return (
                    <ToolGroupCard
                      key={segment.key}
                      activities={segment.activities}
                    />
                  );
                }

                const { item } = segment;
                if (item.kind === 'refresh') {
                  return (
                    <Text
                      key={item.id}
                      size="xs"
                      color="$textSubtle"
                      fontStyle="italic"
                      testID="assistant-refresh-indicator"
                    >
                      {`↻ ${t('admin.assistant.activity.contextRefreshed')}`}
                    </Text>
                  );
                }

                return (
                  <Box
                    key={item.id}
                    alignSelf={item.kind === 'user' ? 'flex-end' : 'flex-start'}
                    bg={item.kind === 'user' ? '$bgBrandSubtle' : '$bgSubtle'}
                    px="$2"
                    py="$2"
                    borderRadius="$sm"
                    maxWidth="90%"
                    testID={`admin-assistant-message-${item.kind}`}
                  >
                    {item.kind === 'assistant' ? (
                      item.content ? (
                        <MarkdownText content={item.content} fontSize={13} />
                      ) : (
                        !!isStreaming && (
                          <Text size="sm">{t('admin.assistant.thinking')}</Text>
                        )
                      )
                    ) : (
                      <Text size="sm">{item.content}</Text>
                    )}
                  </Box>
                );
              })}
              {!!pendingConfirmation && (
                <ConfirmCard
                  pending={pendingConfirmation}
                  onConfirm={confirmPending}
                  isLoading={isStreaming}
                />
              )}
            </VStack>
          </ScrollView>

          <HStack px="$3" py="$2" space="sm" borderTopWidth={1} borderColor="$borderLight">
            <Input flex={1} size="sm" isDisabled={isStreaming}>
              <InputField
                value={draft}
                onChangeText={setDraft}
                placeholder={t('admin.assistant.placeholder')}
                onKeyPress={handleKeyPress}
                testID="admin-assistant-input"
              />
            </Input>
            <Button size="sm" onPress={handleSend} isDisabled={isStreaming || !draft.trim()}>
              <ButtonText>{t('admin.assistant.send')}</ButtonText>
            </Button>
          </HStack>
        </Box>
      )}

      <Pressable
        onPress={() => setIsOpen((open) => !open)}
        testID="admin-assistant-bubble"
        bg="$actionPrimary"
        w={56}
        h={56}
        borderRadius="$full"
        alignItems="center"
        justifyContent="center"
        softShadow="3"
      >
        <Icon as={MessageCircle} size="lg" color="$textOnBrand" />
      </Pressable>
    </Box>
  );
};

export default AdminAssistant;
